import { detectMaterial } from './material-adjust.js';
const necks = ['18', '20', '24', '26', '28', '30', '32', '35', '36', '38', '40', '42', '100'];
const threads = ['400', '410', '415'];
const colours = ['白色', '黑色', '透明', '红色', '绿色'];
/**
 * The standard parameters. Values are kept under these keys, so a renamed parameter keeps its
 * values and the same parameter means the same thing in every category. Two are not only
 * described but used: material decides the benchmark a quote follows, and weight is the
 * item's own unit weight (skus.unit_weight), not a copy of it.
 */
export const SPEC_PARAMS = {
    neck: { label: '口径', options: necks, unit: '口' },
    thread: { label: '螺纹', options: threads, join: '/' },
    cap_type: { label: '盖型', options: ['防盗盖', '翻盖', '按压盖', '喷雾盖', '平盖'] },
    capacity: { label: '容量', options: ['100ml', '200ml', '250ml', '330ml', '500ml', '1L'] },
    material: {
        label: '材质',
        options: ['PP', 'PE', 'HDPE', 'PET'],
        hint: '决定报价联动的原料',
    },
    weight: {
        label: '克重',
        options: [],
        unit: 'g',
        numeric: true,
        hint: '即单重，生产扣料用',
    },
    color: { label: '颜色', options: colours },
    finish: { label: '表面处理', options: ['光面', '条纹'] },
    liner: { label: '内衬', options: ['无', '垫片', '内塞'] },
    shape: { label: '瓶型', options: ['圆瓶', '方瓶', '扁瓶'] },
    grade: { label: '牌号', options: [] },
    pack_type: { label: '类型', options: ['纸箱', '编织袋', '托盘', '缠绕膜', '胶带'] },
    size: { label: '尺寸', options: [] },
};
/** The standard parameter behind a key; an enterprise's own parameters have none. */
export const specParam = (key) => SPEC_PARAMS[key];
/** An enterprise's own parameter is keyed c_ and a few letters, never like a standard one. */
export const CUSTOM_KEY = /^c_[a-z0-9]{4,12}$/;
export const customKey = () => `c_${(Math.random().toString(36) + '000000').slice(2, 8)}`;
/** A template's code prefix: two to four capital letters. */
export const CODE_PREFIX = /^[A-Z]{2,4}$/;
const field = (key, required, own) => ({
    key,
    label: SPEC_PARAMS[key].label,
    options: [...SPEC_PARAMS[key].options],
    required,
    ...own,
});
/** Templates the platform offers to start from; each enterprise edits its own copy. */
export const SPEC_PRESETS = [
    {
        name: '瓶盖',
        prefix: 'CAP',
        fields: [
            field('neck', true),
            field('thread', false),
            field('cap_type', false),
            field('material', true),
            field('weight', false),
            field('color', true),
            field('finish', false),
            field('liner', false),
        ],
    },
    {
        name: '塑料瓶',
        prefix: 'BTL',
        fields: [
            field('capacity', true),
            field('neck', true, { label: '瓶口' }),
            field('thread', false),
            field('material', true, { options: ['PET', 'HDPE', 'PP'] }),
            field('weight', false),
            field('color', true),
            field('shape', false),
        ],
    },
    {
        name: '瓶坯',
        prefix: 'PRF',
        fields: [
            field('weight', true),
            field('neck', true, {
                label: '瓶口',
                options: ['28mm PCO1881', '28mm PCO1810', '30/25', '38mm'],
            }),
            field('material', true, { options: ['PET'] }),
            field('color', true),
        ],
    },
    {
        name: '原料',
        prefix: 'RAW',
        fields: [
            field('material', true, { options: ['PP', 'PE', 'HDPE', 'PET', '色母'] }),
            field('grade', false),
            field('color', false),
        ],
    },
    {
        name: '包材',
        prefix: 'PKG',
        fields: [field('pack_type', true), field('size', false), field('weight', false)],
    },
];
/**
 * The item's spec line from its parameters, in template order: "28/410 · PP · 白色" with a
 * thread, "28口 · PP · 白色" without one. Values are keyed by parameter key.
 */
export function specText(fields, values) {
    const parts = [];
    for (const field of fields) {
        const value = values[field.key]?.trim();
        if (!value)
            continue;
        const param = specParam(field.key);
        const last = parts[parts.length - 1];
        if (param?.join && last) {
            last.value += param.join + value;
            last.unit = param.unit ?? '';
        }
        else
            parts.push({ value, unit: param?.unit ?? '' });
    }
    // A unit already typed with the value (28口, 28 mm) is not added again.
    return parts.map((x) => (x.unit && /\d$/.test(x.value) ? x.value + x.unit : x.value)).join(' · ');
}
/** The benchmark the template's material names, if the template has one and it is followed. */
export function specMaterial(fields, values) {
    const value = fields.some((f) => f.key === 'material') && values.material;
    return value ? detectMaterial(value) : null;
}
